import React, { useEffect, useRef } from 'react';
import {
  Box, Button, CircularProgress, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';
import { requestError } from './messages';
import type { ApiData, SharePointFile, SharePointReviewOption } from './types';

const API = 'api/integrations/sharepoint';
// While a reprocess runs, its row is checked this often (ms) to show the result when the pipeline finishes.
const REPROCESS_POLL_MS = 10000;

interface ChangedFilesSectionProps {
  workspaceId: number;
  changedCount: number;
  onUpdated: () => Promise<void>;
}

interface ChangedFileRowProps {
  file: SharePointFile;
  busy: boolean;
  onReprocess: () => void;
  onDismiss: () => void;
  onDecide: (option: SharePointReviewOption, action: 'keep' | 'not_applicable') => void;
}

interface OptionDecision {
  file: SharePointFile;
  option: SharePointReviewOption;
  action: 'keep' | 'not_applicable';
}

function documentLabel(file: SharePointFile): string {
  if (file.document_type === 'base_lease') {
    return 'Base lease';
  }
  return file.document_type === 'amendment' ? 'Amendment' : '';
}

function optionText(option: SharePointReviewOption): string {
  return option.notice_date === null ? option.label : `${option.label} (notice deadline ${moment(option.notice_date).format('M/D/YYYY')})`;
}

function ChangedFileRow({
  file, busy, onReprocess, onDismiss, onDecide,
}: ChangedFileRowProps): JSX.Element {
  const finished = file.reprocess_status === 'done';

  const action = (): JSX.Element => {
    if (file.reprocess_status === 'running') {
      return (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
        }}
        >
          <CircularProgress size={14} />
          <Typography variant='body2'>Reprocessing…</Typography>
        </Box>
      );
    }
    if (finished) {
      // Dismissing would drop the questions below, so it waits until each option is decided.
      return file.reprocess_review.length > 0
        ? <Typography variant='body2' color='text.secondary'>Decide on the options below</Typography>
        : <Button size='small' disabled={busy} onClick={onDismiss}>Dismiss</Button>;
    }
    if (file.reprocess_status === 'failed') {
      return (
        <>
          <Typography variant='body2' color='error' component='span' sx={{ mr: 1 }}>{file.note !== '' ? file.note : 'Reprocess failed.'}</Typography>
          <Button size='small' variant='outlined' disabled={busy} onClick={onReprocess} sx={{ mr: 1 }}>Try again</Button>
          <Button size='small' disabled={busy} onClick={onDismiss}>Dismiss</Button>
        </>
      );
    }
    return <Button size='small' variant='outlined' disabled={busy} onClick={onReprocess}>Reprocess</Button>;
  };

  return (
    <>
      <TableRow>
        <TableCell sx={finished ? { borderBottom: 'none' } : {}}>
          <Link href={file.web_url} target='_blank' rel='noopener noreferrer'>{file.name}</Link>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{file.path}</Typography>
        </TableCell>
        <TableCell sx={finished ? { borderBottom: 'none' } : {}}>
          {file.lease_tenant_name ?? '—'}
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{documentLabel(file)}</Typography>
        </TableCell>
        <TableCell sx={finished ? { borderBottom: 'none' } : {}}>
          {file.last_modified_at === null ? '—' : moment(file.last_modified_at).format('MMM D, YYYY h:mm a')}
        </TableCell>
        <TableCell align='right' sx={{ whiteSpace: 'nowrap', ...finished ? { borderBottom: 'none' } : {} }}>{action()}</TableCell>
      </TableRow>
      {finished && (
        <TableRow>
          <TableCell colSpan={4} sx={{ pt: 0 }}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>Reprocessed with the new version. What changed:</Typography>
              <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                {file.reprocess_changes.map((line) => <li key={line}><Typography variant='body2'>{line}</Typography></li>)}
              </Box>
              {file.reprocess_review.length > 0 && (
                <>
                  <Typography variant='body2' sx={{ fontWeight: 600, mt: 1.5 }}>
                    Not found in the new version. Strada keeps tracking these until you decide:
                  </Typography>
                  {file.reprocess_review.map((option) => (
                    <Box
                      key={option.option_id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
                      }}
                    >
                      <Typography variant='body2' sx={{ flexGrow: 1 }}>{optionText(option)}</Typography>
                      <Button size='small' disabled={busy} onClick={(): void => { onDecide(option, 'keep'); }}>Keep</Button>
                      <Button size='small' variant='outlined' disabled={busy} onClick={(): void => { onDecide(option, 'not_applicable'); }}>
                        Mark not applicable
                      </Button>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function ChangedFilesSection({
  workspaceId, changedCount, onUpdated,
}: ChangedFilesSectionProps): JSX.Element | null {
  const { enqueueSnackbar } = useSnackbar();
  const { data: files = [] } = useQuery(
    ['get/sharepoint-items', workspaceId, 'changed'],
    async () => axios({ url: `${API}/items/`, method: 'get', params: { workspace: workspaceId, changed: 'true' } }),
    {
      enabled: changedCount > 0,
      select: (res: AxiosResponse<ApiData<SharePointFile[]>>) => res.data.data,
      refetchInterval: (data?: SharePointFile[]): number | false => ((data ?? []).some((f) => f.reprocess_status === 'running') ? REPROCESS_POLL_MS : false),
    },
  );

  // When a reprocess finishes, the lease, the Options Monitor and the counts change too.
  const running = useRef<number[]>([]);
  useEffect(() => {
    const now = files.filter((f) => f.reprocess_status === 'running').map((f) => f.id);
    const finished = running.current.some((id) => !now.includes(id));
    running.current = now;
    if (finished) {
      onUpdated().catch((): void => { /* the next sync refreshes the counts anyway */ });
    }
  }, [files, onUpdated]);

  const onError = (verb: string) => (error: unknown): void => { enqueueSnackbar(requestError(error, verb), { variant: 'error' }); };
  const { mutate: reprocess, isLoading: starting } = useMutation(
    async (file: SharePointFile) => axios({ url: `${API}/items/${file.id}/reprocess/`, method: 'post', params: { workspace: workspaceId } }),
    {
      onSuccess: async (_res: AxiosResponse, file: SharePointFile): Promise<void> => {
        enqueueSnackbar(`Reprocessing "${file.name}" with its new version. This takes a few minutes.`);
        await onUpdated();
      },
      onError: onError('reprocess the file'),
    },
  );
  const { mutate: dismiss, isLoading: dismissing } = useMutation(
    async (file: SharePointFile) => axios({ url: `${API}/items/${file.id}/reprocess/dismiss/`, method: 'post', params: { workspace: workspaceId } }),
    { onSuccess: onUpdated, onError: onError('dismiss the summary') },
  );
  const { mutate: decide, isLoading: deciding } = useMutation(
    async ({ file, option, action }: OptionDecision) => axios({
      url: `${API}/items/${file.id}/reprocess/options/${option.option_id}/`, method: 'post', params: { workspace: workspaceId }, data: { action },
    }),
    {
      onSuccess: async (_res: AxiosResponse, { option, action }: OptionDecision): Promise<void> => {
        enqueueSnackbar(action === 'keep' ? `Still tracking the ${option.label.toLowerCase()}.` : `${option.label} marked not applicable.`);
        await onUpdated();
      },
      onError: onError('update the option'),
    },
  );

  if (changedCount === 0 || files.length === 0) {
    return null;
  }
  return (
    <>
      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
        {`Changed in SharePoint (${files.length})`}
      </Typography>
      <Typography color='text.secondary' sx={{ mb: 1 }}>
        These files were edited or replaced in SharePoint after Strada read them. Reprocess a file to update its lease from the new version; your edits to its options are kept.
      </Typography>
      <Table size='small' sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>File</TableCell>
            <TableCell>Lease</TableCell>
            <TableCell>Changed</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <ChangedFileRow
              key={file.id}
              file={file}
              busy={starting || dismissing || deciding}
              onReprocess={(): void => { reprocess(file); }}
              onDismiss={(): void => { dismiss(file); }}
              onDecide={(option: SharePointReviewOption, action: 'keep' | 'not_applicable'): void => { decide({ file, option, action }); }}
            />
          ))}
        </TableBody>
      </Table>
    </>
  );
}
